// src/components/Replan/hooks/useDrawing.js
import { useCallback, useEffect, useRef } from 'react';
import { TOOLS, BOOLEAN_OPERATIONS } from '../constants/drawingTools';
import { calculateDistance, generateId, resolveShapeScaleProps, recalculateShapeMeasurements } from '../utils/drawingUtils';
import { performBooleanOperation, calculateAreaMeasurements, calculateTotalAreaWithHoles, isSameVertexEdit } from '../utils/booleanOperations';
import { useDrawingState } from './useDrawingState';
import { usePointerHandlers } from './usePointerHandlers';
import { useKeyboardShortcuts } from './useKeyboardShortcuts';

/**
 * @description The primary orchestrator hook for all drawing canvas functionality.
 * This hook integrates state management, pointer events, keyboard shortcuts, and high-level actions.
 * It does not manage state directly but delegates to specialized hooks.
 *
 * @param {React.RefObject} stageRef - Ref to the Konva Stage component.
 * @param {React.RefObject} transformerRef - Ref to the Konva Transformer component.
 * @param {number} stageScale - The current zoom level of the stage.
 * @param {string} projectName - The name of the current project.
 * @param {object} currentPageData - The current page data including DPI information.
 * @param {number} currentImageIndex - The index of the currently displayed image.
 * @param {array} customScales - Custom scales for measurement.
 * @param {object} imageSize - The current image dimensions {width, height}.
 * @returns {object} An object containing all the state and actions needed by the UI components.
 */
export const useDrawing = (stageRef, transformerRef, stageScale, projectName, currentPageData, currentImageIndex, customScales = [], imageSize = { width: 0, height: 0 }, onCreateMeasurementSummary, displaySettings = {}, allScales = []) => {
    // --- HOOK INTEGRATION ---

    // Extract DPI from currentPageData for shape storage
    const currentDPI = currentPageData?.dpi || 72;
    const currentImageSize = imageSize;

    // 1. State Management: All state variables and their setters are managed by useDrawingState.
    const state = useDrawingState(projectName, currentPageData?.version, currentImageIndex, customScales);
    const {
        shapes, setShapes,
        selectedShapeIds, setSelectedShapeIds,
        selectedTool, setSelectedTool,
        lastActiveTool,
        isDrawing, setIsDrawing,
        currentShape, setCurrentShape,
        selectionRect, setSelectionRect,
        vertexEditInfo, setVertexEditInfo,
        originalVertexPosition, setOriginalVertexPosition,
        currentScaleProps,
        isOrthogonalModeActive, setIsOrthogonalModeActive,
        isRectangleMode, setIsRectangleMode,
        isSnappingEnabled, setIsSnappingEnabled,
        isGridVisible, setIsGridVisible,
        strokeWidth, setStrokeWidth,
        insertVertexMode, setInsertVertexMode,
        hoveredSegmentInfo, setHoveredSegmentInfo,
        ...restState // Includes undo/redo, colors, modes, etc.
    } = state;

    // Ref to prevent double-finalization: set by handlePointerUp when it adds a
    // shape, checked by finalizeAndResetDrawing to avoid adding it again.
    const shapeAlreadyFinalizedRef = useRef(false);



    // --- HIGH-LEVEL ACTIONS ---
    // These functions orchestrate complex state changes, often triggered by UI events or keyboard shortcuts.

    /**
     * @description Finalizes the current in-progress shape (e.g., a polyline) and adds it to the canvas.
     * This is used when the user finishes drawing a shape by pressing Spacebar or changing tools.
     */
    const finalizeAndResetDrawing = useCallback(() => {
        if (!isDrawing || !currentShape) return;

        // If handlePointerUp already added this shape to the canvas,
        // just clean up drawing state without adding a duplicate.
        if (shapeAlreadyFinalizedRef.current) {
            shapeAlreadyFinalizedRef.current = false;
            setIsDrawing(false);
            setCurrentShape(null);
            return;
        }

        let shapeToAdd = null;

        // Logic to convert the temporary 'currentShape' into a final, permanent shape.
        switch (currentShape.type) {
            case TOOLS.POLYLINE: {
                const polyline = currentShape;
                const finalPointsAbsolute = polyline.points.slice(0, -2);
                if (finalPointsAbsolute.length >= 4) {
                    const firstX = finalPointsAbsolute[0];
                    const firstY = finalPointsAbsolute[1];
                    const relativePoints = [];
                    for (let i = 0; i < finalPointsAbsolute.length; i += 2) {
                        relativePoints.push(finalPointsAbsolute[i] - firstX, finalPointsAbsolute[i + 1] - firstY);
                    }
                    const finalSegmentDistances = [];
                    if (relativePoints.length >= 4) {
                        for (let i = 0; i < relativePoints.length - 2; i += 2) {
                            finalSegmentDistances.push(calculateDistance(relativePoints[i], relativePoints[i + 1], relativePoints[i + 2], relativePoints[i + 3]));
                        }
                    }
                    const { x, y, points, startX, startY, segmentDistances, ...shapeDataFromCurrent } = polyline;
                    shapeToAdd = {
                        ...shapeDataFromCurrent,
                        points: relativePoints,
                        x: firstX,
                        y: firstY,
                        segmentDistances: finalSegmentDistances,
                        originalDPI: currentDPI, // Store DPI for measurements
                        storedDPI: currentDPI, // Track current coordinate DPI
                    };
                }
                break;
            }
            case TOOLS.POLYGON: {
                const polygon = currentShape;
                if (polygon.isRectangleMode) {
                    // Abort rectangle mode if finalized early (since it requires two clicks)
                    break;
                }
                const finalPoints = polygon.points.slice(0, -2);
                if (finalPoints.length >= 6) {
                    const firstX = finalPoints[0];
                    const firstY = finalPoints[1];
                    const finalDistances = polygon.segmentDistances.slice(0, -2);
                    const lastX = finalPoints[finalPoints.length - 2];
                    const lastY = finalPoints[finalPoints.length - 1];
                    const closingDistance = calculateDistance(lastX, lastY, firstX, firstY);
                    finalDistances.push(closingDistance);
                    const relativePoints = finalPoints.map((p, i) => (i % 2 === 0 ? p - firstX : p - firstY));
                    const { x, y, points, startX, startY, segmentDistances, ...shapeData } = polygon;
                    shapeToAdd = { ...shapeData, points: relativePoints, segmentDistances: finalDistances, x: firstX, y: firstY, originalDPI: currentDPI, storedDPI: currentDPI };
                }
                break;
            }
            // Other cases for Rect, Circle, Line, etc.
            case TOOLS.RECTANGLE:
                if (currentShape.width > 0 || currentShape.height > 0) shapeToAdd = { ...(({ startX, startY, ...rest }) => rest)(currentShape), originalDPI: currentDPI, storedDPI: currentDPI, pendingTextEdit: true };
                break;
            case TOOLS.CIRCLE:
                if (currentShape.radius > 0) shapeToAdd = { ...(({ startX, startY, ...rest }) => ({ ...rest, x: startX, y: startY }))(currentShape), originalDPI: currentDPI, storedDPI: currentDPI };
                break;
            case TOOLS.LINE:
            case TOOLS.MEASURE:
            case TOOLS.COTATION:
            case TOOLS.ARROW:
                const [x1, y1, x2, y2] = currentShape.points;
                if (x1 !== x2 || y1 !== y2) {
                    const { startX, startY, ...rest } = currentShape;
                    shapeToAdd = {
                        ...rest,
                        x: x1,
                        y: y1,
                        points: [0, 0, x2 - x1, y2 - y1],
                        distance: (rest.type === TOOLS.MEASURE || rest.type === TOOLS.COTATION) ? calculateDistance(0, 0, x2 - x1, y2 - y1) : undefined,
                        originalDPI: currentDPI,
                        storedDPI: currentDPI,
                        ...(rest.type === TOOLS.ARROW ? {
                            text: (rest.id.slice(-10) + '\n---\nBody text here...'),
                            pendingTextEdit: true
                        } : {})
                    };
                }
                break;
            case TOOLS.PEN:
                break;
        }

        if (shapeToAdd) setShapes(prev => [...prev, shapeToAdd]);

        // Reset drawing state for the next action.
        shapeAlreadyFinalizedRef.current = false;
        setIsDrawing(false);
        setCurrentShape(null);
    }, [isDrawing, currentShape, setShapes, setCurrentShape, setIsDrawing, currentDPI, shapeAlreadyFinalizedRef]);

    const abortDrawing = useCallback(() => {
        setIsDrawing(false);
        setCurrentShape(null);
        shapeAlreadyFinalizedRef.current = false;
    }, [setIsDrawing, setCurrentShape]);

    /**
     * @description Handles the logic for selecting a new drawing tool.
     * It finalizes any ongoing drawing operation before switching tools.
     */
    const selectTool = useCallback((tool) => {
        // If we're currently drawing, handle special-case rectangle-mode POLYGON abort.
        if (isDrawing && currentShape) {
            // If we're drawing a POLYGON in rectangle mode (first corner placed),
            // Cancel the drawing instead of finalizing when switching tools (or pressing Esc).
            const isActiveRectanglePolygon =
                selectedTool === TOOLS.POLYGON &&
                state.isRectangleMode &&
                currentShape.type === TOOLS.POLYGON &&
                currentShape.isRectangleMode;

            if (isActiveRectanglePolygon) {
                // Abort rectangle creation
                abortDrawing();
            } else {
                // Finalize any other in-progress drawing
                finalizeAndResetDrawing();
            }
        }

        // Reset interaction states.
        if (state.snapIndicator) state.setSnapIndicator(null);
        if (state.visibleSnapPoints?.length > 0) state.setVisibleSnapPoints([]);
        if (vertexEditInfo) {
            setVertexEditInfo(null);
            state.setOriginalVertexPosition(null);
        }

        // Deselect shapes when changing to a new tool.
        if (tool !== selectedTool) {
            setSelectedShapeIds([]);
        }

        // Set the new tool.
        setSelectedTool(tool);
    }, [selectedTool, isDrawing, currentShape, vertexEditInfo, state, finalizeAndResetDrawing, setSelectedShapeIds, setSelectedTool, setVertexEditInfo, setIsDrawing, setCurrentShape]);


    // 2. Pointer Handlers: Manages low-level pointer events (down, move, up, dblclick).
    const pointerActions = {
        setIsDrawing, setCurrentShape, setShapes, setSelectionRect, setSelectedShapeIds, finalizeAndResetDrawing, abortDrawing,
        setSnapIndicator: state.setSnapIndicator, setVisibleSnapPoints: state.setVisibleSnapPoints,
        setVertexEditInfo, setOriginalVertexPosition: state.setOriginalVertexPosition,
        setInsertVertexMode, setHoveredSegmentInfo,
        shapeAlreadyFinalizedRef,
    };
    const { handlePointerDown, handlePointerMove, handlePointerUp, handleDoubleClick } = usePointerHandlers(
        { ...state, stageScale, dpi: currentDPI, imageSize: currentImageSize, selectedPointMarker: state.selectedPointMarker, allScales }, // Pass full state + dynamic stageScale + measurement context + allScales
        pointerActions,
        stageRef
    );

    // --- UI-FACING EVENT HANDLERS ---
    // These handlers are passed directly to Konva components in the JSX.

    const handleStrokeColorChange = useCallback((newColor) => {
        state.setDrawingStrokeColor(newColor);
        if (selectedShapeIds.length > 0) {
            setShapes(prevShapes =>
                prevShapes.map(shape =>
                    selectedShapeIds.includes(shape.id) ? { ...shape, stroke: newColor } : shape
                )
            );
        }
    }, [selectedShapeIds, setShapes, state.setDrawingStrokeColor]);

    const handleStrokeWidthChange = useCallback((newWidth) => {
        state.setStrokeWidth(newWidth);
        if (selectedShapeIds.length > 0) {
            setShapes(prevShapes =>
                prevShapes.map(shape =>
                    selectedShapeIds.includes(shape.id) ? { ...shape, strokeWidth: newWidth } : shape
                )
            );
        }
    }, [selectedShapeIds, setShapes, state.setStrokeWidth]);

    // 3. Keyboard Shortcuts: Listens for global key events.
    const keyboardActions = {
        setShapes, setSelectedShapeIds, setVertexEditInfo,
        setOriginalVertexPosition: state.setOriginalVertexPosition,
        setSnapIndicator: state.setSnapIndicator,
        setVisibleSnapPoints: state.setVisibleSnapPoints,
        selectTool, finalizeAndResetDrawing, abortDrawing, setIsOrthogonalModeActive,
        setIsRectangleMode, setIsSnappingEnabled, setIsGridVisible,
        onCreateMeasurementSummary,
        handleStrokeWidthChange,
        setDisplayKeys: displaySettings.setDisplayKeys,
        setDisplayUnitSystem: displaySettings.setDisplayUnitSystem,
        setDisplayKeysPanelVisible: displaySettings.setDisplayKeysPanelVisible,
        setInsertVertexMode,
    };
    useKeyboardShortcuts({
        ...state,
        dpi: currentDPI,
        imageSize: currentImageSize,
        stageRef,
        displayKeys: displaySettings.displayKeys,
        displayUnitSystem: displaySettings.displayUnitSystem,
        measurementSystem: displaySettings.measurementSystem,
        displayKeysPanelVisible: displaySettings.displayKeysPanelVisible,
        allScales,
    }, keyboardActions);

    // Insert-vertex mode requires exactly one selected AREA/POLYLINE shape
    useEffect(() => {
        const selectedShape = shapes.find(s => s.id === selectedShapeIds[0]);
        const validSelection =
            selectedShapeIds.length === 1 &&
            selectedShape &&
            (selectedShape.type === TOOLS.AREA || selectedShape.type === TOOLS.POLYLINE);

        if (insertVertexMode && (!validSelection || selectedTool !== TOOLS.SELECT)) {
            if (vertexEditInfo && vertexEditInfo.isInserted) {
                setShapes(prevShapes =>
                    prevShapes.map(shape => {
                        if (shape.id === vertexEditInfo.shapeId) {
                            const shapeScaleProps = resolveShapeScaleProps(vertexEditInfo.originalShape, currentScaleProps, allScales);
                            return recalculateShapeMeasurements(vertexEditInfo.originalShape, shapeScaleProps, currentDPI, currentImageSize);
                        }
                        return shape;
                    })
                );
                setVertexEditInfo(null);
                setOriginalVertexPosition(null);
            }
            setInsertVertexMode(false);
            setHoveredSegmentInfo(null);
        } else if (!insertVertexMode) {
            setHoveredSegmentInfo(null);
        }
    }, [
        insertVertexMode, selectedShapeIds, shapes, setInsertVertexMode, setHoveredSegmentInfo,
        vertexEditInfo, setVertexEditInfo, setOriginalVertexPosition,
        currentScaleProps, allScales, currentDPI, currentImageSize, selectedTool,
    ]);

    const handleShapeClick = useCallback((e) => {
        if (selectedTool !== TOOLS.SELECT) return;
        
        // Let middle clicks bubble up to the Stage for panning
        if (e.evt && e.evt.button === 1) {
            return;
        }

        e.cancelBubble = true;
        const shapeId = e.target.id();
        if (!shapeId) return;

        const metaPressed = e.evt.shiftKey || e.evt.ctrlKey || e.evt.metaKey;
        setSelectedShapeIds(prev => {
            const isSelected = prev.includes(shapeId);
            if (metaPressed) {
                return isSelected ? prev.filter(id => id !== shapeId) : [...prev, shapeId];
            } else {
                return isSelected ? prev : [shapeId];
            }
        });
    }, [selectedTool, setSelectedShapeIds]);

    const handleVertexClick = useCallback((e, shapeId, vertexIndex, isHole = false, holeIndex = 0) => {
        // Let middle clicks bubble up to the Stage for panning
        if (e.evt && e.evt.button === 1) {
            return;
        }

        // Right-click handling: if there's an active vertex edit, allow event to bubble for abort
        if (e.evt && e.evt.button === 2) {
            if (vertexEditInfo) {
                // Let the right-click bubble up to handlePointerDown for abort logic
                return;
            } else {
                // No active edit, just ignore right-click
                e.cancelBubble = true;
                return;
            }
        }

        e.cancelBubble = true;

        // Only respond to left mouse button (button 0) or touch events
        if (e.evt && e.evt.button !== undefined && e.evt.button !== 0) {
            return;  // Ignore middle-clicks and other buttons
        }

        // Use functional state update to get the latest value and avoid stale closure issues
        setVertexEditInfo(currentEdit => {
            const isSameVertex = isSameVertexEdit(currentEdit, shapeId, vertexIndex, isHole, holeIndex);

            // If clicking the same vertex, deactivate edit mode (user is done editing)
            if (isSameVertex) {
                state.setOriginalVertexPosition(null);
                return null;  // Clear the edit info
            }

            // Otherwise, activate edit mode for the clicked vertex (this clears any previous edit)
            const shape = shapes.find(s => s.id === shapeId);
            if (shape && (
                shape.type === TOOLS.POLYLINE || 
                shape.type === TOOLS.AREA ||
                shape.type === TOOLS.MEASURE ||
                shape.type === TOOLS.COTATION ||
                shape.type === TOOLS.ARROW
            )) {
                let originalRelX, originalRelY;
                if (isHole && shape.holes && shape.holes[holeIndex]) {
                    originalRelX = shape.holes[holeIndex][vertexIndex * 2];
                    originalRelY = shape.holes[holeIndex][vertexIndex * 2 + 1];
                } else {
                    if (shape.type === TOOLS.COTATION && vertexIndex === 2) {
                        const [x1, y1, x2, y2] = shape.points;
                        const dx = x2 - x1;
                        const dy = y2 - y1;
                        const L = Math.sqrt(dx * dx + dy * dy);
                        const currentOffset = shape.offsetDist !== undefined ? shape.offsetDist : (30 / stageScale);
                        const nx = L > 0 ? -dy / L : 0;
                        const ny = L > 0 ? dx / L : 0;
                        originalRelX = (x1 + x2) / 2 + currentOffset * nx;
                        originalRelY = (y1 + y2) / 2 + currentOffset * ny;
                    } else {
                        originalRelX = shape.points[vertexIndex * 2];
                        originalRelY = shape.points[vertexIndex * 2 + 1];
                    }
                }
                state.setOriginalVertexPosition({ x: originalRelX, y: originalRelY });
                return { shapeId, vertexIndex, isHole, holeIndex };  // Set new edit info
            }

            return currentEdit;  // Keep current state if shape not found
        });
    }, [vertexEditInfo, shapes, state.setOriginalVertexPosition, stageScale]);

    const handleShapeDragEnd = useCallback((e) => {
        const node = e.target;
        const draggedShapeId = node.id();
        if (!draggedShapeId) return;

        const originalShape = shapes.find(s => s.id === draggedShapeId);
        if (!originalShape) return;

        const newPos = node.position();
        const dx = newPos.x - originalShape.x;
        const dy = newPos.y - originalShape.y;

        if (dx === 0 && dy === 0) return;

        setShapes(prevShapes =>
            prevShapes.map(shape => {
                if (selectedShapeIds.includes(shape.id)) {
                    if (shape.id === draggedShapeId) {
                        return { ...shape, x: newPos.x, y: newPos.y };
                    }
                    return { ...shape, x: shape.x + dx, y: shape.y + dy };
                }
                return shape;
            })
        );
    }, [shapes, selectedShapeIds, setShapes]);

    const executeBooleanOperation = useCallback((operation) => {
        const selectedPolygonShapes = shapes.filter(shape =>
            selectedShapeIds.includes(shape.id) && (shape.type === TOOLS.AREA)
        ).sort((a, b) => calculateTotalAreaWithHoles(b.points, b.holes) - calculateTotalAreaWithHoles(a.points, a.holes));

        if (selectedPolygonShapes.length < 2) return;

        // Fold: start from the largest shape, combine the rest one at a time.
        // performBooleanOperation returns an array of components (one per
        // disconnected outer boundary), so multi-piece results are preserved.
        let results = [selectedPolygonShapes[0]];
        for (let i = 1; i < selectedPolygonShapes.length; i++) {
            results = performBooleanOperation(results, selectedPolygonShapes[i], operation);
            if (!results) { console.warn(`Boolean operation '${operation}' failed.`); return; }
            if (results.length === 0) { console.warn(`Boolean operation '${operation}' produced no geometry.`); return; }
        }

        const newAreaShapes = results.map(result => ({
            id: generateId(), type: TOOLS.AREA, ...result,
            segmentDistances: calculateAreaMeasurements(result.points),
            stroke: state.drawingStrokeColor, strokeWidth: state.strokeWidth, rotation: 0,
            sourceOperation: operation, sourcePolygons: selectedShapeIds.slice(),
            visible: true
        }));

        setShapes(prevShapes => [
            ...prevShapes.filter(shape => !selectedShapeIds.includes(shape.id)),
            ...newAreaShapes
        ]);
        setSelectedShapeIds(newAreaShapes.map(shape => shape.id));
    }, [selectedShapeIds, shapes, state.drawingStrokeColor, state.strokeWidth, setShapes, setSelectedShapeIds]);

    const handleTransformEnd = useCallback((e) => {
        const node = e.target;
        if (!node) return;

        const scaleX = node.scaleX();
        const scaleY = node.scaleY();
        node.scaleX(1);
        node.scaleY(1);

        setShapes(prev =>
            prev.map(shape => {
                if (shape.id === node.id()) {
                    let updatedAttrs = { x: node.x(), y: node.y(), rotation: node.rotation() };
                    switch (shape.type) {
                        case TOOLS.RECTANGLE:
                            updatedAttrs = { ...updatedAttrs, width: Math.max(50, node.width() * scaleX), height: Math.max(50, node.height() * scaleY) };
                            break;
                        case TOOLS.CIRCLE:
                            updatedAttrs = { ...updatedAttrs, radius: Math.max(5, (shape.radius || 1) * ((scaleX + scaleY) / 2)) };
                            break;
                        case TOOLS.LINE: case TOOLS.MEASURE: case TOOLS.COTATION: case TOOLS.ARROW:
                            const [p0, p1, p2, p3] = shape.points;
                            const newRelX = p2 * scaleX;
                            const newRelY = p3 * scaleY;
                            updatedAttrs = { ...updatedAttrs, points: [p0, p1, newRelX, newRelY] };
                            if (shape.type === TOOLS.MEASURE || shape.type === TOOLS.COTATION) {
                                updatedAttrs.distance = calculateDistance(p0, p1, newRelX, newRelY);
                            }
                            if (shape.type === TOOLS.COTATION && shape.offsetDist !== undefined) {
                                updatedAttrs.offsetDist = shape.offsetDist * ((scaleX + scaleY) / 2);
                            }
                            break;
                        case TOOLS.POLYLINE:
                            const scaledPolyPoints = shape.points.map((p, idx) => idx % 2 === 0 ? p * scaleX : p * scaleY);
                            const newSegmentDistances = [];
                            if (scaledPolyPoints.length >= 4) {
                                for (let i = 0; i < scaledPolyPoints.length - 2; i += 2) {
                                    newSegmentDistances.push(calculateDistance(scaledPolyPoints[i], scaledPolyPoints[i + 1], scaledPolyPoints[i + 2], scaledPolyPoints[i + 3]));
                                }
                            }
                            updatedAttrs = { ...updatedAttrs, points: scaledPolyPoints, segmentDistances: newSegmentDistances };
                            break;
                    }
                    return { ...shape, ...updatedAttrs };
                }
                return shape;
            })
        );


        if (vertexEditInfo && originalVertexPosition) {
            setShapes(prevShapes =>
                prevShapes.map(shape => {
                    if (shape.id === vertexEditInfo.shapeId &&
                        (
                            shape.type === TOOLS.POLYLINE || 
                            shape.type === TOOLS.AREA ||
                            shape.type === TOOLS.MEASURE ||
                            shape.type === TOOLS.COTATION ||
                            shape.type === TOOLS.ARROW
                        )) {
                        const updatedShape = updateMultiPointShapeOnVertexDrag(
                            shape,
                            vertexEditInfo.vertexIndex,
                            originalVertexPosition.x,
                            originalVertexPosition.y,
                            vertexEditInfo.isHole || false,
                            vertexEditInfo.holeIndex || 0
                        );
                        const shapeScaleProps = resolveShapeScaleProps(updatedShape, currentScaleProps, allScales);
                        return recalculateShapeMeasurements(updatedShape, shapeScaleProps, currentDPI, imageSize);
                    }
                    return shape;
                })
            );
            setVertexEditInfo(null);
            setOriginalVertexPosition(null);
            if (snapIndicator) setSnapIndicator(null);
            setVisibleSnapPoints([]);
            return;
        }

    }, [setShapes, vertexEditInfo, originalVertexPosition, setOriginalVertexPosition, currentScaleProps, allScales, currentDPI, imageSize]);

    const resetDrawingState = useCallback(() => {
        setShapes([]);
        setCurrentShape(null);
        setSelectedShapeIds([]);
        setIsDrawing(false);
    }, [setShapes, setCurrentShape, setSelectedShapeIds, setIsDrawing]);

    // --- FINAL EXPORT ---
    // The returned object is the public API of the drawing feature.
    return {
        // State
        ...restState,
        currentScaleProps,
        shapes,
        selectedShapeIds,
        selectedTool,
        lastActiveTool,
        isDrawing,
        currentShape,
        selectionRect,
        strokeColor: state.displayStrokeColor,
        strokeWidth,
        setStrokeWidth,
        isOrthogonalModeActive,
        isRectangleMode,
        setIsRectangleMode,
        isSnappingEnabled,
        setIsSnappingEnabled,
        isGridVisible,
        setIsGridVisible,
        snapIndicator: state.snapIndicator,
        visibleSnapPoints: state.visibleSnapPoints,
        vertexEditInfo, // Export vertex edit info for ShapeRenderer
        insertVertexMode,
        setInsertVertexMode,
        hoveredSegmentInfo,
        setHoveredSegmentInfo,
        selectedPointMarker: state.selectedPointMarker,
        setSelectedPointMarker: state.setSelectedPointMarker,
        // Actions & Handlers
        setShapes,
        setCurrentShape,
        setIsDrawing,
        setSelectedShapeIds,
        selectTool,
        handleStrokeColorChange,
        handleStrokeWidthChange,
        handlePointerDown,
        handlePointerMove,
        handlePointerUp,
        handleDoubleClick,
        handleShapeClick,
        handleShapeDragEnd,
        handleVertexClick,
        handleTransformEnd,
        resetDrawingState,
        abortDrawing,
        setIsOrthogonalModeActive,
        executeBooleanOperation,
        BOOLEAN_OPERATIONS,
    };
};
